import { rgba } from 'polished';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { t } from 'ttag';
import useBasketContext from '../../../../../contexts/basket/useBasketContext';
import { Item } from '../../../../../contexts/site/site-context';
import { money } from '../../../../../services/format';
import {iLog} from "../../../../../index";
import useOperatorContext from "../../../../../contexts/operator/useOperatorContext";
import useSiteContext from "../../../../../contexts/site/useSiteContext";
import useGroupContext from "../../../../../contexts/group/useGroupContext";

type OrderItemProps = {
    item: Item;
    index: number;
    showItemModal: (item: Item, edit: boolean) => void;
    unavailableScheduledItem: boolean;
};

const OrderItem = ({ item, index, showItemModal, unavailableScheduledItem }: OrderItemProps) => {
    const basketContext = useBasketContext();
    const operatorContext = useOperatorContext();
    const siteContext = useSiteContext();
    const groupContext = useGroupContext();

    // guests can't change anything while the lead is choosing a time or checking out
    const locked = groupContext.active && !groupContext.isLead && groupContext.locked;

    const [quantity, setQuantity] = useState(item?.quantity ?? 1);

    useEffect( () => {
        if ( quantity < 1 ) {
            iLog('OrderItem: quantity now 0. Remove item.', item);
            basketContext.removeItem(item);
            return;
        }

        if ( item.quantity !== quantity ) {
            iLog('OrderItem: quantity has changed', [item.name, quantity, item.quantity !== quantity]);
            basketContext.addItem(item, quantity, true);
        }
    }, [ quantity ])

    useEffect( () => {
        if ( item.quantity ) {
            setQuantity(item.quantity);
        }
    }, [ item ]);

    const handleQuantityClick = (e: any, value: number) => {
        e.stopPropagation();

        if ( locked ) return;

        setQuantity(quantity + value);
    };

    return (
        <Layout onClick={() => {
            showItemModal(item, true)
        }}>

            <Details>
                <div className="line-item">
                    <div className="b-item-values">
                        <QuantityWrapper>
                            { quantity > 1 ? (
                                <button onClick={(e) => handleQuantityClick(e, -1)} type="button" aria-label="Decrease Quantity" disabled={locked}>
                                    <span className="material-symbols-outlined">do_not_disturb_on</span>
                                </button>
                            ) : (
                                <button onClick={(e) => handleQuantityClick(e, -1)} type="button" aria-label="Decrease Quantity" disabled={locked}>
                                    <span className="material-symbols-outlined">delete</span>
                                </button>
                            )}
                            <span>{quantity}</span>
                            <button onClick={(e) => handleQuantityClick(e, 1)} type="button" aria-label="Increase Quantity" disabled={locked}>
                                <span className="material-symbols-outlined">add_circle</span>
                            </button>
                        </QuantityWrapper>
                    </div>
                    <div className="b-item-name">
                        <span className="b-item-name-text">{item.name}</span>
                        {item.modifiers?.length ? (
                            <div className="b-item-mod">
                                {item.modifiers?.map((modifier, index) => {

                                    // item's group
                                    let group = item.modifier_groups.find((mg) => mg.uid === modifier.group_uid);

                                    if ( group && !group?.multi_choice && modifier.quantity === -1 ) {
                                        return '';
                                    }

                                    return <span key={`basket-item-mod-${modifier.uid}`}>
                                        { modifier.quantity === -1 ? 'No '+ modifier.name : modifier.name }
                                        { modifier.quantity > 1 ? ' ('+ modifier.quantity +')' : '' }
                                        { item.modifiers && index < item.modifiers?.length - 1 ? ', ' : ''}
                                    </span>;
                                })}
                            </div>
                        ) : null}
                        {item.groupLine && !item.groupLine.mine && item.groupLine.participant && (
                            <div className="b-item-guest">{t`Added by ${item.groupLine.participant}`}</div>
                        )}
                        {item.groupLine?.problem && (
                            <span className="b-item__error">
                                {item.groupLine.mine
                                    ? t`Sorry, ${item.groupLine.problem} is no longer available. Please remove it to continue.`
                                    : t`Sorry, ${item.groupLine.problem} is no longer available. Please remove it, or ask ${item.groupLine.participant} to.`}
                            </span>
                        )}
                        {unavailableScheduledItem && (
                            <span className="b-item__error">{t`This item is not available at your chosen ${['delivery', 'catering', 'venues'].includes(siteContext.channel) ? 'delivery' : 'collection'} time.`}</span>
                        )}
                    </div>
                    <div className="b-item-values">
                        <div className="b-item-price">
                            {money(item.price * (item.quantity ?? 1) + (item.modifiersTotal ?? 0) * (item.quantity ?? 1), operatorContext.operator?.ccy, operatorContext.operator?.locale)}
                        </div>
                    </div>
                </div>
            </Details>
        </Layout>
    );
};

const ModalWrapper = styled.div`
    .modal-content {
        padding: 0 !important;
    }

    .closeBtn {
        cursor: pointer;
        opacity: 1;
    }

    button.close {
        display: none;
    }
`;

const QuantityWrapper = styled.div`
    user-select: none;
    display: flex;
    align-items: center;
    gap: 4px;
    span {
        font-size: 16px;
        line-height: 20px;
        display: inline-flex;
        text-align: center;
        justify-content: center;
        color: var(--text-black);

        &.material-symbols-outlined {
            font-size: 24px;
            line-height: 24px;
            color: var(--color_ui_5);
        }
    }
    button {
        padding: 0;
        border: none;
        margin: 0;
        font: inherit;
        color: inherit;
        background-color: transparent;
        text-align: inherit;
        cursor: pointer;
        height: 24px;
        &:focus {
            outline: none;
        }
        &:disabled {
            opacity: 0.4;
            cursor: not-allowed;
        }
    }
`;

const Details = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;

    .line-item {
        display: flex;
        gap: 12px;
        align-items: flex-start;
    }

    .b-item__error {
        color: var(--invalid);
        display: flex;
        gap: 8px;
        align-items: center;
    }

    .b-item-name {
        flex-grow: 1;
    }

    .b-item-price {
        min-width: 74px;
        text-align: right;
    }

    .b-item-name,
    .b-item-price {
        font-size: 16px;
        line-height: 24px;
        color: var(--text-black);
    }

    .b-item-values {
        flex-shrink: 0;
        gap: 12px;
        display: flex;
        align-items: center;
    }

    .b-item-guest {
        font-size: 14px;
        line-height: 20px;
        color: var(--text-grey);
        font-style: italic;
    }

    .b-item-mod {
        font-size: 14px;
        line-height: 20px;
        color: var(--text-grey);
        font-weight: 400;
    }
`;

const Layout = styled.div`
    width: 100%;
    cursor: pointer;
    padding: 8px;

    &:hover {
        background-color: var(--bg-grey);

        .b-item-name-text {
            text-decoration: underline;
        }
    }
`;

export default OrderItem;
